import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { 
    AcademicCapIcon, 
    BookOpenIcon,
    UserGroupIcon,
    ComputerDesktopIcon,
    ArrowRightIcon
} from '@heroicons/react/24/outline';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';

const ProgramsSection: React.FC = () => {
    const { t } = useTranslation();
    const sectionRef = useRef<HTMLDivElement>(null);
    const [isVisible, setIsVisible] = useState(false);

    useEffect(() => {
        const observer = new IntersectionObserver(
            (entries) => {
                if (entries[0].isIntersecting) {
                    setIsVisible(true);
                }
            },
            { threshold: 0.1 }
        );

        if (sectionRef.current) {
            observer.observe(sectionRef.current);
        }

        return () => {
            if (sectionRef.current) {
                observer.unobserve(sectionRef.current);
            }
        };
    }, []);

    const programs = [
        {
            id: 'saturday',
            icon: AcademicCapIcon,
            iconBackground: 'bg-gradient-to-br from-blue-500 to-blue-700',
            cardBackground: 'bg-gradient-to-br from-blue-500 to-blue-600',
            href: '/programs'
        },
        {
            id: 'afternoon',
            icon: BookOpenIcon,
            iconBackground: 'bg-gradient-to-br from-green-500 to-green-700',
            cardBackground: 'bg-gradient-to-br from-green-500 to-green-600',
            href: '/programs'
        },
        {
            id: 'summer',
            icon: UserGroupIcon,
            iconBackground: 'bg-gradient-to-br from-purple-500 to-purple-700',
            cardBackground: 'bg-gradient-to-br from-purple-500 to-purple-600',
            href: '/programs'
        },
        {
            id: 'online',
            icon: ComputerDesktopIcon,
            iconBackground: 'bg-gradient-to-br from-orange-500 to-orange-700',
            cardBackground: 'bg-gradient-to-br from-orange-500 to-orange-600',
            href: '/programs'
        }
    ];

    const variants = {
        hidden: { opacity: 0 },
        visible: { 
            opacity: 1,
            transition: { 
                staggerChildren: 0.3
            }
        }
    };

    const cardVariants = {
        hidden: { y: 50, opacity: 0 },
        visible: { 
            y: 0, 
            opacity: 1,
            transition: { duration: 0.6 }
        }
    };

    return (
        <div className="py-24 sm:py-32 relative overflow-hidden bg-gradient-to-b from-white to-gray-50" ref={sectionRef}>
            {/* 装饰元素 - 背景几何图形 */}
            <div className="absolute inset-0 z-0 opacity-10">
                <div className="absolute -top-20 -right-20 w-96 h-96 rounded-full bg-blue-700"></div>
                <div className="absolute top-40 -left-20 w-64 h-64 rounded-full bg-purple-600"></div>
                <div className="absolute bottom-20 right-40 w-80 h-80 rounded-full bg-indigo-600"></div>
            </div>
            
            <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8">
                <motion.div 
                    className="max-w-2xl text-center mx-auto"
                    initial={{ opacity: 0, y: 30 }}
                    animate={isVisible ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
                    transition={{ duration: 0.5 }}
                >
                    <motion.h2 
                        className="text-base font-bold leading-7 text-white bg-blue-600 inline-block px-4 py-1 rounded-full shadow-lg"
                        whileHover={{ scale: 1.05 }}
                        transition={{ duration: 0.2 }}
                    >
                        {t('programs.section.title')}
                    </motion.h2>
                    <motion.p 
                        className="mt-6 text-3xl font-extrabold tracking-tight text-gray-900 sm:text-4xl"
                        initial={{ opacity: 0, y: 20 }}
                        animate={isVisible ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
                        transition={{ duration: 0.5, delay: 0.2 }}
                    >
                        <span className="inline-block relative">
                            <span className="relative z-10">{t('programs.section.subtitle')}</span>
                            <span className="absolute bottom-2 left-0 w-full h-3 bg-blue-100 -z-10 transform -rotate-1"></span>
                        </span>
                    </motion.p>
                </motion.div>
                
                <motion.div 
                    className="mt-16 grid grid-cols-1 gap-6 sm:mt-20 sm:grid-cols-2 lg:mx-0 lg:grid-cols-4 lg:gap-8"
                    variants={variants}
                    initial="hidden"
                    animate={isVisible ? "visible" : "hidden"}
                >
                    {programs.map((program, index) => (
                        <motion.div
                            key={program.id}
                            variants={cardVariants}
                            className="h-full"
                            whileHover={{ scale: 1.03, y: -5 }}
                            transition={{ duration: 0.2 }}
                        >
                            <Link
                                to={program.href}
                                className={`relative isolate flex flex-col gap-6 h-full rounded-2xl ${program.cardBackground} p-6 shadow-xl transition-all duration-300 backdrop-blur-sm`}
                            >
                                <div className="absolute inset-0 -z-10 rounded-2xl bg-white/10 opacity-30"></div>
                                <div className={`flex h-14 w-14 items-center justify-center rounded-xl ${program.iconBackground} shadow-lg`}>
                                    <program.icon className="h-7 w-7 text-white" aria-hidden="true" />
                                </div>
                                <div>
                                    <h3 className="text-xl font-bold leading-7 text-white">
                                        {t(`programs.section.items.${program.id}.title`)}
                                    </h3>
                                    <p className="mt-2 text-base leading-7 text-white/90">
                                        {t(`programs.section.items.${program.id}.description`)}
                                    </p>
                                </div>
                                <div className="mt-auto">
                                    {/* <div className="flex items-center gap-x-4">
                                        <div className="text-sm leading-6 text-white/90 px-2 py-1 rounded-full bg-white/20">
                                            {t(`programs.section.items.${program.id}.level`)}
                                        </div>
                                        <div className="text-sm leading-6 text-white/90 px-2 py-1 rounded-full bg-white/20">
                                            {t(`programs.section.items.${program.id}.duration`)}
                                        </div>
                                    </div> */}
                                    <div className="mt-4 flex items-center gap-x-2 text-sm font-semibold leading-6 text-white group">
                                        {t(`programs.section.items.${program.id}.action`)}
                                        <ArrowRightIcon className="h-4 w-4 transition-transform group-hover:translate-x-1" aria-hidden="true" />
                                    </div>
                                </div>
                            </Link>
                        </motion.div>
                    ))}
                </motion.div>
            </div>
        </div>
    );
};

export default ProgramsSection; 